/**
 * Botao/gesto de VOLTAR fecha o modal, em vez de sair do app.
 *
 * No Android o botao de voltar (e no iOS o swipe da borda) e a forma esperada
 * de sair de uma camada. Antes disto o app nao escutava `popstate` em lugar
 * nenhum: voltar com um modal aberto saia da aplicacao inteira, descartando
 * qualquer formulario em andamento. Ver docs/12-auditoria-mobile-professor.md
 * (achado C5, item "a").
 *
 * COMO FUNCIONA
 * Ao abrir, empurra uma entrada no historico marcada com um id proprio. A URL
 * NAO muda (`window.location.href` de novo) — importante porque o App decide
 * qual pagina renderizar lendo `window.location.pathname` uma unica vez na
 * montagem; mudar o path aqui quebraria isso. O projeto tambem nao usa
 * react-router, entao `popstate` estava inteiramente livre.
 *
 * O PROBLEMA DIFICIL: CASCATA ENTRE CAMADAS
 * Com um modal (A) e um sub-modal (B) abertos, o historico e [..., A, B].
 * Fechar B pela UI precisa remover a entrada de B — senao o botao voltar vira
 * um no-op silencioso. Mas chamar history.back() dispara `popstate`, e o
 * listener de A ainda esta registrado: A fecharia junto.
 *
 * A solucao NAO e olhar quem e o topo (quando o popstate chega, a camada de
 * cima ja desmontou e a de baixo ja e o topo). Cada camada grava a sua
 * PROFUNDIDADE no state e so fecha quando a profundidade corrente fica menor
 * que a sua — ou seja, quando a sua propria entrada ficou pra tras.
 *
 *   base: profundidade 0  ->  A abre: 1  ->  B abre: 2  ->  C abre: 3
 *   voltar: profundidade corrente vira 2
 *     - C: 2 < 3  -> fecha        (correto)
 *     - B: 2 < 2  -> permanece    (correto)
 *     - A: 2 < 1? nao -> permanece (correto)
 *
 * Comparar por IDENTIDADE ("a entrada corrente ainda e a minha?") em vez de
 * profundidade parece equivalente e passa no teste de duas camadas — mas
 * quebra com tres: a camada A, dois niveis abaixo, ve um id que nao e o dela
 * e fecha junto. Ver /tmp de testes do cenario C4 na implementacao.
 *
 * @param {boolean}  isOpen   camada esta visivel?
 * @param {Function} onClose  fecha ESTA camada (nao a pilha inteira)
 */
import { useEffect, useRef } from "react";

let seq = 0;

export function useHistoryBackClose(isOpen, onClose) {
  // Ref para o callback: sem isso, um onClose recriado a cada render do pai
  // re-executaria o effect e empurraria uma entrada nova no historico a cada
  // tecla digitada dentro do modal.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  // R220: timer de um history.back() adiado pela cleanup (ver comentario
  // grande la embaixo) — precisa sobreviver entre re-execucoes do effect pra
  // poder ser cancelado se o proprio hook remontar no mesmo ciclo.
  const pendingBackRef = useRef(null);

  useEffect(() => {
    // Reclama um history.back() que a cleanup do monte anterior tinha
    // ADIADO (nao disparado ainda) — cobre o StrictMode do React 18 em dev,
    // que monta -> desmonta -> monta de novo, SINCRONAMENTE, so pra testar
    // resiliencia de cleanup. Sem isso, o back() adiado do primeiro monte
    // disparava depois, batendo na entrada que o SEGUNDO monte tinha acabado
    // de empurrar. Ver motivo completo do adiamento no comentario da cleanup.
    if (pendingBackRef.current) {
      clearTimeout(pendingBackRef.current);
      pendingBackRef.current = null;
    }

    if (!isOpen) return undefined;
    if (typeof window === "undefined" || !window.history?.pushState) return undefined;

    const id = `modal-${++seq}`;
    // Profundidade = quantas camadas ja estao empilhadas + 1. Sai do proprio
    // state corrente, entao acompanha a pilha sem precisar de contador global.
    const depth = (window.history.state?.__modalDepth ?? 0) + 1;

    // Mesma URL, so o state muda — ver comentario sobre pathname acima.
    window.history.pushState(
      { __modalLayer: id, __modalDepth: depth },
      "",
      window.location.href,
    );

    let fechadoPeloVoltar = false;

    const onPop = () => {
      const atual = window.history.state?.__modalDepth ?? 0;
      // Ainda estou na minha profundidade (ou abaixo dela): quem saiu foi uma
      // camada acima da minha, nao eu.
      if (atual >= depth) return;
      fechadoPeloVoltar = true;
      onCloseRef.current?.();
    };

    window.addEventListener("popstate", onPop);

    return () => {
      window.removeEventListener("popstate", onPop);

      // Fechou pela UI (X, Escape, salvar): a entrada empurrada continua no
      // historico e precisa sair, senao o proximo "voltar" nao faz nada.
      //
      // O guard importa: so mexe no historico se a entrada corrente ainda for
      // a nossa. Se nao for, ou ja foi removida pelo proprio voltar, ou outra
      // coisa navegou por cima — em ambos os casos chamar back() aqui levaria
      // o usuario pra tras sem que ele tenha pedido.
      //
      // R220 (bug real, Chrome/Edge): antes disto o back() era chamado NA
      // HORA, aqui na cleanup. history.back() e ASSINCRONO — o popstate real
      // so chega depois. No StrictMode (dev), a cleanup do primeiro monte
      // rodava, agendava esse back(), e o SEGUNDO monte (sincrono, logo em
      // seguida) ja empurrava outra entrada por cima. Quando o back()
      // finalmente processava, ele navegava de volta pro estado ANTERIOR ao
      // modal — e o listener do SEGUNDO monte via uma profundidade menor que
      // a sua e fechava o modal na hora ("pisca e fecha"). Chrome/Edge
      // parecem processar essa navegacao pendente mesmo com um pushState mais
      // novo por cima; o Firefox, na pratica, nao repetia o sintoma (mais
      // tolerante a chamadas de historico em sequencia rapida) — dai o bug
      // ser so em Chrome/Edge. Adiando o back() pro proximo tick e permitindo
      // que um remonte no mesmo ciclo CANCELE esse timer resolve pros dois
      // casos: StrictMode (cancela, sem back() nenhum) e fechamento real
      // (dispara normalmente, um tick depois — imperceptivel pro usuario).
      if (!fechadoPeloVoltar && window.history.state?.__modalLayer === id) {
        pendingBackRef.current = setTimeout(() => {
          pendingBackRef.current = null;
          window.history.back();
        }, 0);
      }
    };
  }, [isOpen]);
}

export default useHistoryBackClose;
